import useSWR from 'swr';

import { fetchEndpointData } from '@/utils/fetch.client';

/**
 * The spend ledger for a space over a window.
 *
 * Every figure originates from the same rows a charge would be raised from
 * (`llm_usage` + `tool_usage`), so the headline total and the breakdown tables
 * under it are partitions of one number rather than independent estimates.
 *
 * Money is integer **micro-USD** end to end — the unit the ledger stores. Format
 * at the edge with `formatUsd`; never round in transit.
 */
export interface UsageTotals {
  /** Authoritative spend. Already includes `tool_micro_usd` — don't add them. */
  total_micro_usd: number;
  /** Model-call spend, part of the total. */
  llm_micro_usd: number;
  /** Metered-capability spend (web search), part of the total. */
  tool_micro_usd: number;
  request_count: number;
  tool_request_count: number;
  prompt_tokens: number;
  completion_tokens: number;
  cached_prompt_tokens: number;
  /**
   * Calls billed as zero because the provider reported no usage. Non-zero means
   * the total is a *lower bound*, which the UI says out loud rather than
   * presenting an exact-looking figure that isn't.
   */
  unpriced_request_count: number;
  unpriced_tool_request_count: number;
  /**
   * Appended billing corrections and their net effect (negative for reversals).
   * The ledger is append-only, so this is what explains a total below the sum of
   * the charges listed above it.
   */
  correction_count: number;
  corrections_micro_usd: number;
}

/** One member's slice of a single day, for the stacked bars. */
export interface DailyUserSlice {
  /** `null` for ledger rows written without an owning user (agent tokens). */
  user_id: string | null;
  total_micro_usd: number;
}

/** One calendar day (UTC). Days with no spend are present, so the axis is even. */
export interface DailyUsage extends UsageTotals {
  /** `YYYY-MM-DD`. */
  date: string;
  per_user: DailyUserSlice[];
}

export interface UserUsage extends UsageTotals {
  user_id: string | null;
  name: string | null;
  email: string | null;
  avatar_url: string | null;
  session_count: number;
  last_activity_at: string | null;
}

export interface ModelUsage extends UsageTotals {
  /** Alias the request was billed under. */
  model: string;
  /** Catalogue name, falling back to the alias for retired models. */
  display_name: string;
}

export interface ToolUsage extends UsageTotals {
  provider: string;
  operation: string;
  /** Provider-defined billable units (e.g. searches). */
  units: number;
}

export interface SessionUsage extends UsageTotals {
  session_id: string;
  name: string | null;
  user_id: string | null;
  user_name: string | null;
  repository_name: string | null;
  model: string | null;
  status: string | null;
  last_activity_at: string | null;
}

export interface TeamUsage extends UsageTotals {
  /** Inclusive window bounds, `YYYY-MM-DD`. */
  start: string;
  end: string;
  daily: DailyUsage[];
  by_user: UserUsage[];
  by_model: ModelUsage[];
  by_tool: ToolUsage[];
  /** Top spenders only — compare with `session_count` for what is omitted. */
  by_session: SessionUsage[];
  session_count: number;
  active_user_count: number;
}

export interface UsageRange {
  /** Inclusive, `YYYY-MM-DD`. */
  start: string;
  /** Inclusive, `YYYY-MM-DD`. */
  end: string;
  /** Narrow every breakdown to one member. */
  userId?: string | null;
}

export function teamUsageKey(teamId: string, range: UsageRange): string {
  const params = new URLSearchParams({ start: range.start, end: range.end });
  if (range.userId) params.set('user_id', range.userId);
  return `/api/v1/teams/${teamId}/usage?${params}`;
}

/**
 * A space's usage ledger for a window.
 *
 * Deliberately not polled: spend is read here for review, not enforcement (that
 * lives on the session budget), and re-fetching a month of ledger rows on a timer
 * costs far more than the staleness is worth.
 */
export function useTeamUsage(teamId: string | null, range: UsageRange) {
  const key = teamId ? teamUsageKey(teamId, range) : null;
  const { data, error, isLoading, mutate } = useSWR(key, () => fetchEndpointData<TeamUsage>(key!), {
    revalidateOnFocus: false,
    keepPreviousData: true,
  });
  return { usage: data ?? null, error, isLoading, mutate };
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const MICRO_USD_PER_USD = 1_000_000;

export function microToUsd(micro: number): number {
  return micro / MICRO_USD_PER_USD;
}

/**
 * Money, at the precision the magnitude deserves.
 *
 * Sub-cent amounts keep four decimals instead of collapsing to `$0.00`: a ledger
 * row that reads as zero when it isn't is exactly the kind of thing that makes
 * people stop trusting the page. Totals in the thousands drop to whole dollars,
 * because cents on a $2,122 figure are noise.
 */
export function formatUsd(micro: number, { compact = false }: { compact?: boolean } = {}): string {
  const usd = microToUsd(micro);
  const abs = Math.abs(usd);
  const sign = usd < 0 ? '-' : '';

  if (usd === 0) return '$0.00';
  if (abs < 0.01) return `${sign}$${abs.toFixed(4)}`;
  if (compact && abs >= 1_000) return `${sign}$${Math.round(abs).toLocaleString('en-US')}`;
  return `${sign}$${abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Axis/tick labels, where width matters more than precision. */
export function formatUsdAxis(micro: number): string {
  const usd = microToUsd(micro);
  if (usd === 0) return '$0';
  if (Math.abs(usd) >= 1_000) return `$${(usd / 1_000).toFixed(usd % 1_000 === 0 ? 0 : 1)}k`;
  if (Math.abs(usd) < 1) return `$${usd.toFixed(2)}`;
  return `$${Math.round(usd).toLocaleString('en-US')}`;
}

/** Token counts, which run to the hundreds of millions. */
export function formatTokens(n: number): string {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return `${n}`;
}

export function formatCount(n: number): string {
  return n.toLocaleString('en-US');
}

// ---------------------------------------------------------------------------
// Ranges
// ---------------------------------------------------------------------------

/**
 * Window presets. The backend refuses windows past ~6 months rather than
 * clamping them, so nothing here may exceed that.
 */
export type RangePreset = '7d' | '30d' | '90d' | 'mtd';

export const RANGE_PRESETS: { value: RangePreset; label: string }[] = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: 'mtd', label: 'This month' },
];

/** `YYYY-MM-DD` in UTC — the same calendar the ledger buckets days by. */
export function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function resolveRange(preset: RangePreset, today = new Date()): { start: string; end: string } {
  const end = toIsoDate(today);
  if (preset === 'mtd') {
    const first = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
    return { start: toIsoDate(first), end };
  }
  const days = preset === '7d' ? 7 : preset === '90d' ? 90 : 30;
  const start = new Date(today.getTime() - (days - 1) * 86_400_000);
  return { start: toIsoDate(start), end };
}

/** `2026-08-10` → `Aug 10`. Parsed as UTC so the label matches the bucket. */
export function formatDayLabel(iso: string, { withYear = false }: { withYear?: boolean } = {}): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(withYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  });
}
